import { motion } from "framer-motion";
import { useState } from "react";
import { SEO } from "@/components/SEO";
import { Calendar, Clock, User, BookOpen, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BLOG_ARTICLES_DATA } from "@/lib/index";
import { IMAGES } from "@/assets/images";

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { type: "spring", stiffness: 300, damping: 35 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { type: "spring", stiffness: 300, damping: 35 },
};

const categorieLabels = {
  coparentalite: 'Coparentalité',
  mediation: 'Médiation',
  expertise: 'Expertise',
  enfants: 'Enfants',
  conseils: 'Conseils',
};

const categorieColors = {
  coparentalite: 'bg-primary/10 text-primary',
  mediation: 'bg-secondary/10 text-secondary',
  expertise: 'bg-accent/10 text-accent-foreground',
  enfants: 'bg-destructive/10 text-destructive',
  conseils: 'bg-muted text-muted-foreground',
};

export default function Blog() {
  const [selectedCategorie, setSelectedCategorie] = useState<string | null>(null);

  const filteredArticles = selectedCategorie
    ? BLOG_ARTICLES_DATA.filter(article => article.categorie === selectedCategorie)
    : BLOG_ARTICLES_DATA;

  const categories = Array.from(new Set(BLOG_ARTICLES_DATA.map(a => a.categorie)));

  return (
    <div className="min-h-screen">
      <SEO
        title="Ressources et articles — Coparentalité, médiation et famille"
        description="Articles et ressources sur la coparentalité, la médiation familiale, le bien-être des enfants lors de la séparation. Conseils pratiques par nos experts en Outaouais."
        keywords="articles coparentalité Québec, ressources médiation familiale, blog travailleur social, conseils séparation enfants, garde partagée"
        canonical="/blog"
      />
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.CONSULTATION_1}
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/90 to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem}>
              <Badge className="mb-4">Ressources</Badge>
            </motion.div>
            <motion.h1
              className="text-5xl md:text-6xl font-bold mb-6"
              variants={staggerItem}
            >
              Blog et ressources
            </motion.h1>
            <motion.p
              className="text-xl text-muted-foreground max-w-3xl mx-auto"
              variants={staggerItem}
            >
              Articles, conseils et informations pour vous accompagner dans votre parcours familial. Des ressources pratiques rédigées par nos experts.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            className="flex flex-wrap gap-3 justify-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 300, damping: 35 }}
          >
            <Button
              variant={selectedCategorie === null ? "default" : "outline"}
              onClick={() => setSelectedCategorie(null)}
            >
              Tous les articles
            </Button>
            {categories.map((categorie) => (
              <Button
                key={categorie}
                variant={selectedCategorie === categorie ? "default" : "outline"}
                onClick={() => setSelectedCategorie(categorie)}
              >
                {categorieLabels[categorie as keyof typeof categorieLabels]}
              </Button>
            ))}
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {filteredArticles.map((article) => (
              <motion.div key={article.id} variants={staggerItem}>
                <Card className="h-full hover:shadow-lg transition-all duration-200 group">
                  <CardContent className="p-0">
                    <div className="aspect-video bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                      <BookOpen className="h-16 w-16 text-primary/30" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className={categorieColors[article.categorie as keyof typeof categorieColors]}>
                          {categorieLabels[article.categorie as keyof typeof categorieLabels]}
                        </Badge>
                      </div>
                      <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
                        {article.titre}
                      </h3>
                      <p className="text-muted-foreground mb-4 line-clamp-3">
                        {article.description}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>{new Date(article.datePublication).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>{article.tempsLecture}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="h-4 w-4" />
                          <span>{article.auteur}</span>
                        </div>
                        <Button variant="ghost" size="sm" className="group-hover:text-primary">
                          Lire
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 300, damping: 35 }}
          >
            <h2 className="text-3xl font-bold mb-4">Besoin d'un accompagnement personnalisé?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Nos articles vous donnent des pistes, mais chaque situation est unique. Contactez-nous pour un accompagnement adapté à vos besoins.
            </p>
            <Button asChild size="lg">
              <a href="#/contact">
                Prendre rendez-vous
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
